import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Fuel, Gauge, Cog, Car as CarIcon, ShieldCheck, Camera } from "lucide-react";

const BODY_TYPE_LABELS = { sedan: "Sedan", suv: "SUV", ute: "Ute", hatch: "Hatch", wagon: "Wagon", van: "Van", coupe: "Coupe", convertible: "Convertible", other: "Other" };
const TRANSMISSION_LABELS = { auto: "Automatic", manual: "Manual" };
const FUEL_LABELS = { petrol: "Petrol", diesel: "Diesel", hybrid: "Hybrid", ev: "Electric", lpg: "LPG" };

const apiBase = (api.defaults?.baseURL || "").replace(/\/api\/?$/, "");
export const vehiclePhotoUrl = (u) => (u && u.startsWith("/") ? `${apiBase}${u}` : u);

export const formatPrice = (v) => {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v);
};

export default function VehicleListPage({ mode }) {
  // mode = "sale" | "rent"
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState(null);
  const [filters, setFilters] = useState({ make: "all", body: "all" });

  useEffect(() => {
    api.get(`/vehicles?status=${mode}`).then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
  }, [mode]);

  const items = vehicles || [];
  const makes = Array.from(new Set(items.map((v) => v.make))).sort();
  const bodies = Array.from(new Set(items.map((v) => v.body_type).filter(Boolean))).sort();
  const filtered = items.filter((v) =>
    (filters.make === "all" || v.make === filters.make) &&
    (filters.body === "all" || v.body_type === filters.body)
  );

  const title = mode === "sale" ? "Cars for sale." : "Cars for rent.";
  const tagline = mode === "sale"
    ? "Hand-picked, mechanic-checked. Every car comes with a full service history."
    : "Daily and weekly rentals. $500 refundable bond. 1 week minimum hire. Conditions apply.";

  return (
    <div data-testid={`vehicle-list-${mode}`} className="bg-background">
      <section className="border-b border-zinc-900 bg-zinc-950/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-brand-red" />
            <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">
              {mode === "sale" ? "Showroom" : "Rentals"}
            </span>
          </div>
          <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[0.9]">
            {mode === "sale" ? <>Hand-picked <span className="text-brand-red">cars for sale.</span></> : <>Drive away today, <span className="text-brand-red">on rent.</span></>}
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300">{tagline}</p>
        </div>
      </section>

      {/* Filters */}
      {items.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 pr-2">Filter</span>
            <select data-testid="filter-make" value={filters.make} onChange={(e) => setFilters({ ...filters, make: e.target.value })} className="ta-svc-input text-xs font-mono uppercase tracking-widest">
              <option value="all">All makes ({items.length})</option>
              {makes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {bodies.length > 0 && (
              <select data-testid="filter-body" value={filters.body} onChange={(e) => setFilters({ ...filters, body: e.target.value })} className="ta-svc-input text-xs font-mono uppercase tracking-widest">
                <option value="all">All body types</option>
                {bodies.map((b) => <option key={b} value={b}>{BODY_TYPE_LABELS[b] || b}</option>)}
              </select>
            )}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {vehicles === null ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-16 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="vehicle-list-empty" className="border border-zinc-900 bg-zinc-950 p-12 text-center">
            <CarIcon className="h-8 w-8 text-brand-red mx-auto mb-4" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {items.length === 0 ? "Catalog coming soon" : "No matches"}
            </div>
            <h3 className="font-display uppercase text-2xl text-white tracking-tight">
              {items.length === 0
                ? (mode === "sale" ? "We're loading up the showroom" : "We're prepping our rental fleet")
                : "Try a different filter"}
            </h3>
            <p className="mt-3 text-sm text-zinc-400">
              {items.length === 0 ? (
                <>Call us on (03) 6288 7861 — we&apos;ll let you know as soon as one lands.</>
              ) : null}
            </p>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 text-white font-mono text-xs uppercase tracking-widest hover:border-brand-red hover:text-brand-red">
              Contact us <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((v) => <VehicleCard key={v.id} v={v} mode={mode} />)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid={`vehicle-seo-copy-${mode}`}>
        <div className="border-t border-zinc-900 pt-10 max-w-3xl">
          <h2 className="font-display uppercase text-2xl tracking-tight text-white">
            {mode === "sale" ? "Workshop-checked used cars in Glenorchy" : "Rental cars from a workshop you can trust"}
          </h2>
          {mode === "sale" ? (
            <>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                Buying a used car in Hobart shouldn't feel like a gamble. Every vehicle we list has been through our own
                Repco Authorised workshop at 46 Grove Rd, Glenorchy — inspected, serviced and honestly described by the
                same mechanics who'll look after it for you down the track. No yard tricks, no pressure: come and see the
                car, take it for a drive, and ask us anything about its condition.
              </p>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Stock changes regularly, so if you don't see what you're after, send us an enquiry or call
                (03) 6288 7861 — we're happy to keep an eye out for the right car for your budget. We can also arrange a
                pre-purchase inspection on any car you're considering elsewhere.
              </p>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Every sale car comes with its known history laid out plainly — kilometres, rego status, recent work and
                anything still on the to-do list. Because we're a working mechanical shop first, you can also book your
                first service with us and keep the car's logbook history running in one place from day one. Trade-in?
                Bring it along and we'll give you a straight answer on what it's worth.
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                Need wheels while your car is in for repairs, waiting on an insurance claim, or hosting visitors in
                Hobart? Our small rental fleet is maintained in-house at our Glenorchy workshop, so every car goes out
                serviced, safe and clean — with flexible daily and weekly terms and none of the airport-counter runaround.
              </p>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Rentals are popular with locals from Glenorchy, Moonah, Claremont and greater Hobart. Send an enquiry on
                any car above or call (03) 6288 7861 to check availability — pick-up is from 46 Grove Rd, Glenorchy.
              </p>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Hiring is simple: a refundable bond, a minimum hire period shown on each car, and a quick condition
                check at pick-up and return. Because the fleet lives in our own workshop, if anything plays up during
                your hire you deal directly with the mechanics who know the car — not a call centre. Longer-term and
                weekly rates are available on most vehicles; just ask when you enquire.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function VehicleCard({ v, mode }) {
  const price = mode === "rent"
    ? <><span className="text-brand-red">{formatPrice(v.price)}</span><span className="text-sm text-zinc-500"> /day</span></>
    : <span className="text-brand-red">{formatPrice(v.price)}</span>;
  return (
    <Link to={`/vehicles/${v.id}`} data-testid={`vehicle-card-${v.id}`} className="group block border border-zinc-900 bg-zinc-950 hover:border-brand-red transition-colors overflow-hidden">
      <div className="relative aspect-[16/10] bg-zinc-900 overflow-hidden">
        {v.cover_photo ? (
          <img src={vehiclePhotoUrl(v.cover_photo)} alt={v.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="h-full w-full grid place-items-center text-zinc-700">
            <Camera className="h-10 w-10" />
          </div>
        )}
        {v.is_sold && (
          <span className="absolute top-3 left-3 px-3 py-1 bg-black/80 border border-brand-red text-brand-red font-mono text-[10px] uppercase tracking-widest">
            Sold
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display uppercase text-lg tracking-tight text-white leading-tight">{v.title}</h3>
          <div className="font-display text-xl whitespace-nowrap">{price}</div>
        </div>
        <ul className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs font-mono text-zinc-400">
          {v.kilometres != null && <li className="flex items-center gap-1.5"><Gauge className="h-3 w-3 text-brand-red" /> {Intl.NumberFormat("en-AU").format(v.kilometres)} km</li>}
          {v.transmission && <li className="flex items-center gap-1.5"><Cog className="h-3 w-3 text-brand-red" /> {TRANSMISSION_LABELS[v.transmission]}</li>}
          {v.fuel && <li className="flex items-center gap-1.5"><Fuel className="h-3 w-3 text-brand-red" /> {FUEL_LABELS[v.fuel]}</li>}
          {v.body_type && <li className="flex items-center gap-1.5"><CarIcon className="h-3 w-3 text-brand-red" /> {BODY_TYPE_LABELS[v.body_type]}</li>}
        </ul>
        {mode === "rent" && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-800 px-2 py-1">
            <ShieldCheck className="h-3 w-3 text-brand-red" /> Bond {formatPrice(v.bond_amount || 500)} · Min {v.min_rent_days || 7} days
          </div>
        )}
      </div>
    </Link>
  );
}
